import {
  formatKstDateTime,
  formatKstTime,
  toKstDateString,
  type AdminTeamAttendanceRecords,
  type Attendance,
  type AttendanceSegment,
} from '@aegislab/shared';
import { formatDurationHHMM } from '@/shared/lib/format';
import { getUtcDateString } from '@/shared/lib/format';

// ============================================
// 팀 출퇴근 기록 → 엑셀(1일 1행). 모든 시각은 KST.
// 라이브러리(write-excel-file)는 받기 버튼을 누른 순간에만 동적으로 불러온다.
// ============================================

/** write-excel-file `CellObject` 호환 최소형. value 에 null 을 넣지 않는다(빈 칸은 ''). */
export type RecordCell = {
  value: string | number;
  fontWeight?: 'bold';
  wrap?: boolean;
  height?: number;
  backgroundColor?: string;
};

export interface RecordSheet {
  sheet: string;
  data: RecordCell[][];
  stickyRowsCount?: number;
  stickyColumnsCount?: number;
  columns?: { width: number }[];
}

export interface AttendanceRecordInput {
  teamName: string;
  startDate: string;
  endDate: string;
  members: AdminTeamAttendanceRecords['members'];
  records: Attendance[];
  /** 요약 머리에 붙일 조회 시각 표기(선택) */
  queriedAt?: string;
}

const GAP_FILL = '#FDF1C9';
const BREAK_FILL = '#F1ECFA';
const MERGE_TOLERANCE_MS = 1000;
const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];

const text = (value: string | number): RecordCell => ({ value });
const bold = (value: string): RecordCell => ({ value, fontWeight: 'bold' });

const ms = (iso: string) => new Date(iso).getTime();
const roundMin = (v: number) => Math.round(v / 60_000) * 60_000;

/** 'N시간 MM분' — 1분 반올림 */
function longDuration(v: number): string {
  const total = Math.round(Math.max(0, v) / 60_000);
  return `${Math.floor(total / 60)}시간 ${String(total % 60).padStart(2, '0')}분`;
}
/** 'H:MM' — 1분 반올림 */
const shortDuration = (v: number) => formatDurationHHMM(roundMin(v));

const dayNumber = (date: string) =>
  Date.UTC(+date.slice(0, 4), +date.slice(5, 7) - 1, +date.slice(8, 10)) / 86_400_000;

function weekday(date: string): string {
  return WEEKDAYS[new Date(`${date}T00:00:00Z`).getUTCDay()] ?? '';
}

function datesInRange(start: string, end: string): string[] {
  const out: string[] = [];
  for (let d = dayNumber(start); d <= dayNumber(end); d++) {
    out.push(getUtcDateString(new Date(d * 86_400_000)));
  }
  return out;
}

/** 'HH:MM' + 기록 날짜보다 n일 뒤면 '(+n)' */
function timeLabel(iso: string, recordDate: string): string {
  const d = new Date(iso);
  const n = dayNumber(toKstDateString(d)) - dayNumber(recordDate);
  return n > 0 ? `${formatKstTime(d)}(+${n})` : formatKstTime(d);
}

interface Session {
  start: string;
  end: string | null;
}

interface DayCalc {
  record: Attendance;
  absent: boolean;
  open: boolean;
  segments: AttendanceSegment[];
  sessions: Session[];
  workMs: number;
  breakMs: number;
  clubMs: number;
  gaps: { start: string; end: string }[];
  gapMs: number;
  spanMs: number | null;
  workCount: number;
}

function calcDay(record: Attendance): DayCalc {
  const absent = record.status === 'ABSENT';
  const open =
    !absent &&
    (record.status === 'CLOCKED_IN' || record.status === 'ON_BREAK' || record.clockOutAt === null);
  const segments = [...record.segments].sort((a, b) => ms(a.startAt) - ms(b.startAt));
  const sumOf = (type: string) =>
    segments.reduce((s, x) => (x.type === type && x.duration != null ? s + x.duration : s), 0);

  // 이어지는 구간(동아리 포함)을 세션으로 묶는다 — 세션 사이가 재출근 공백.
  const sessions: Session[] = [];
  const gaps: { start: string; end: string }[] = [];
  let last: Session | undefined;
  for (const s of segments) {
    if (last && (last.end === null || ms(s.startAt) <= ms(last.end) + MERGE_TOLERANCE_MS)) {
      if (last.end !== null)
        last.end = s.endAt === null || ms(s.endAt) > ms(last.end) ? s.endAt : last.end;
      continue;
    }
    if (last?.end) gaps.push({ start: last.end, end: s.startAt });
    last = { start: s.startAt, end: s.endAt };
    sessions.push(last);
  }
  // 표시용 첫 시작·마지막 끝은 출퇴근 시각으로 바꾼다(공백은 위에서 구간 시각으로 이미 구함).
  const first = sessions[0] ?? { start: record.clockInAt, end: record.clockOutAt };
  if (sessions.length === 0) sessions.push(first);
  first.start = record.clockInAt;
  const end = sessions[sessions.length - 1];
  // 퇴근 전이면 구간이 모두 닫혀 있어도 끝은 미퇴근이다(세그먼트 시트는 실제 시각을 보인다).
  if (end) end.end = open ? null : record.clockOutAt;

  return {
    record,
    absent,
    open,
    segments,
    sessions,
    workMs: sumOf('WORK'),
    breakMs: sumOf('BREAK'),
    clubMs: sumOf('CLUB'),
    gaps,
    gapMs: gaps.reduce((sum, g) => sum + ms(g.end) - ms(g.start), 0),
    spanMs:
      !absent && !open && record.clockOutAt ? ms(record.clockOutAt) - ms(record.clockInAt) : null,
    workCount: segments.filter((s) => s.type === 'WORK').length,
  };
}

function sessionsLabel(day: DayCalc): string {
  const date = day.record.date;
  return day.sessions
    .map((s) => `${timeLabel(s.start, date)}–${s.end === null ? '미퇴근' : timeLabel(s.end, date)}`)
    .join(' / ');
}

function statusCellText(day: DayCalc): string {
  if (day.absent) return '결근';
  if (day.open) return `${sessionsLabel(day)}\n진행 중`;
  const breakPart = day.breakMs >= 30_000 ? ` · 휴식 ${shortDuration(day.breakMs)}` : '';
  return `${sessionsLabel(day)}\n근무 ${shortDuration(day.workMs)}${breakPart}`;
}

function timeCellText(day: DayCalc): string {
  if (day.absent) return '결근';
  if (day.spanMs === null) return '진행 중';
  return longDuration(day.spanMs);
}

function checkText(day: DayCalc): string {
  if (day.absent) return '';
  const notes: string[] = [];
  if (day.spanMs !== null) {
    // 동아리 구간도 출퇴근 사이를 채우므로 어긋남 계산에 넣는다(순근무·휴식에는 안 넣음).
    const diff = Math.abs(day.spanMs - (day.workMs + day.breakMs + day.clubMs + day.gapMs));
    if (diff > 60_000) {
      notes.push(
        `출퇴근 시각과 근무·휴식 구간이 ${Math.round(diff / 60_000)}분 어긋납니다. 순근무는 구간 기준입니다.`
      );
    }
  }
  if (day.breakMs >= 3 * 3_600_000) {
    notes.push(
      `휴식이 ${shortDuration(day.breakMs)} 입니다. 휴식 종료를 누르지 않았을 수 있습니다.`
    );
  }
  return notes.join(' ');
}

function statusLabel(day: DayCalc): string {
  if (day.absent) return '결근';
  if (!day.open) return '퇴근 완료';
  return day.record.status === 'ON_BREAK' ? '휴식 중' : '출근 중';
}

const SEGMENT_KIND: Record<string, string> = { WORK: '근무', BREAK: '휴식', CLUB: '동아리' };

export function buildAttendanceRecordSheets(input: AttendanceRecordInput): RecordSheet[] {
  const { teamName, startDate, endDate, members } = input;
  const dates = datesInRange(startDate, endDate);
  const memberIds = new Set(members.map((m) => m.userId));
  const byKey = new Map<string, DayCalc>();
  for (const r of input.records) {
    if (memberIds.has(r.userId)) byKey.set(`${r.userId}|${r.date}`, calcDay(r));
  }
  const dayOf = (userId: string, date: string) => byKey.get(`${userId}|${date}`);

  // 1. 요약
  const summary: RecordCell[][] = [
    [bold(`${teamName} 출퇴근 기록`)],
    [text(`기간 ${startDate} ~ ${endDate}`)],
  ];
  if (input.queriedAt) summary.push([text(`조회 ${input.queriedAt}`)]);
  summary.push([]);
  summary.push(
    [
      '구성원',
      '출근일',
      '결근',
      '미퇴근',
      '퇴근−출근 합',
      '휴식 합',
      '재출근 일수',
      '순근무 합',
      '평균 순근무',
    ].map(bold)
  );
  for (const m of members) {
    let workDays = 0;
    let absentDays = 0;
    let openDays = 0;
    let closedDays = 0;
    let reclockDays = 0;
    let span = 0;
    let brk = 0;
    let work = 0;
    for (const date of dates) {
      const day = dayOf(m.userId, date);
      if (!day) continue;
      if (day.absent) {
        absentDays++;
        continue;
      }
      workDays++;
      if (day.sessions.length > 1) reclockDays++;
      if (day.open) {
        openDays++;
        continue;
      }
      closedDays++;
      span += day.spanMs ?? 0;
      brk += day.breakMs;
      work += day.workMs;
    }
    summary.push([
      text(m.userName),
      text(workDays),
      text(absentDays),
      text(openDays),
      text(longDuration(span)),
      text(longDuration(brk)),
      text(reclockDays),
      text(longDuration(work)),
      text(closedDays ? longDuration(work / closedDays) : ''),
    ]);
  }
  summary.push([]);
  summary.push([
    text(
      '순근무는 근무 구간을 더한 값입니다. 휴식과 재출근 공백은 빼고, 퇴근하지 않은 날은 합계에 넣지 않습니다.'
    ),
  ]);

  // 2·3. 날짜 × 구성원 행렬
  const matrixHeader = ['날짜', '요일', ...members.map((m) => m.userName)].map(bold);
  const matrixColumns = [{ width: 12 }, { width: 5 }, ...members.map(() => ({ width: 24 }))];

  const statusRows: RecordCell[][] = [matrixHeader];
  const timeRows: RecordCell[][] = [matrixHeader];
  for (const date of dates) {
    const days = members.map((m) => dayOf(m.userId, date));
    const twoLine = days.some((d) => d && !d.absent);
    const rowStyle: Partial<RecordCell> = twoLine ? { height: 32 } : {};
    statusRows.push([
      { value: date, ...rowStyle },
      { value: weekday(date), ...rowStyle },
      ...days.map((d): RecordCell => ({
        value: d ? statusCellText(d) : '',
        wrap: true,
        ...rowStyle,
        ...(d && !d.absent && d.sessions.length > 1 ? { backgroundColor: GAP_FILL } : {}),
      })),
    ]);
    timeRows.push([
      text(date),
      text(weekday(date)),
      ...days.map((d) => text(d ? timeCellText(d) : '')),
    ]);
  }

  // 4·5. 원천 상세·세그먼트 — 날짜, 구성원 순
  const ordered: { name: string; day: DayCalc }[] = [];
  for (const date of dates) {
    for (const m of members) {
      const day = dayOf(m.userId, date);
      if (day) ordered.push({ name: m.userName, day });
    }
  }

  const detail: RecordCell[][] = [
    [
      '날짜',
      '요일',
      '구성원',
      '출근일시',
      '퇴근일시',
      '상태',
      '퇴근−출근',
      '휴식',
      '재출근 공백',
      '순근무',
      '근무 구간 수',
      '확인 필요',
    ].map(bold),
  ];
  for (const { name, day } of ordered) {
    const r = day.record;
    const base = [text(r.date), text(weekday(r.date)), text(name)];
    if (day.absent) {
      detail.push([
        ...base,
        text(''),
        text(''),
        text('결근'),
        ...Array.from({ length: 6 }, () => text('')),
      ]);
      continue;
    }
    detail.push([
      ...base,
      text(formatKstDateTime(new Date(r.clockInAt))),
      text(day.open || !r.clockOutAt ? '미퇴근' : formatKstDateTime(new Date(r.clockOutAt))),
      text(statusLabel(day)),
      text(timeCellText(day)),
      text(longDuration(day.breakMs)),
      text(longDuration(day.gapMs)),
      text(longDuration(day.workMs)),
      text(day.workCount),
      text(checkText(day)),
    ]);
  }

  const segRows: RecordCell[][] = [
    ['날짜', '요일', '구성원', '순번', '종류', '시작', '종료', '시간', '비고'].map(bold),
  ];
  for (const { name, day } of ordered) {
    const date = day.record.date;
    const items: {
      kind: string;
      start: string;
      end: string | null;
      dur: number | null;
      note: string;
    }[] = day.segments.map((s) => ({
      kind: SEGMENT_KIND[s.type] ?? s.type,
      start: s.startAt,
      end: s.endAt,
      dur: s.duration,
      note: '',
    }));
    for (const g of day.gaps) {
      items.push({
        kind: '재출근 공백',
        start: g.start,
        end: g.end,
        dur: ms(g.end) - ms(g.start),
        note: '퇴근 후 같은 날 다시 출근한 공백(계산값)',
      });
    }
    items.sort((a, b) => ms(a.start) - ms(b.start));
    items.forEach((it, idx) => {
      const fill =
        it.kind === '휴식' ? BREAK_FILL : it.kind === '재출근 공백' ? GAP_FILL : undefined;
      const cell = (value: string | number): RecordCell =>
        fill ? { value, backgroundColor: fill } : { value };
      segRows.push([
        cell(date),
        cell(weekday(date)),
        cell(name),
        cell(idx + 1),
        cell(it.kind),
        cell(timeLabel(it.start, date)),
        cell(it.end === null ? '' : timeLabel(it.end, date)),
        cell(it.end === null || it.dur === null ? '진행 중' : longDuration(it.dur)),
        cell(it.note),
      ]);
    });
  }

  return [
    { sheet: '요약', data: summary, columns: [{ width: 16 }] },
    {
      sheet: '날짜별 현황',
      data: statusRows,
      stickyRowsCount: 1,
      stickyColumnsCount: 2,
      columns: matrixColumns,
    },
    {
      sheet: '시간 계산',
      data: timeRows,
      stickyRowsCount: 1,
      stickyColumnsCount: 2,
      columns: matrixColumns,
    },
    { sheet: '원천 상세', data: detail, stickyRowsCount: 1 },
    { sheet: '세그먼트', data: segRows, stickyRowsCount: 1 },
  ];
}

export function attendanceRecordFileName(teamName: string, start: string, end: string): string {
  const safe = teamName.replace(/[\\/:*?"<>|]/g, '_').trim() || '팀';
  return `${safe}_출퇴근기록_${start}_${end}.xlsx`;
}

/** 파일 생성·다운로드. 라이브러리는 여기서 동적으로 부른다. */
export async function downloadAttendanceRecordXlsx(input: AttendanceRecordInput): Promise<void> {
  const { default: writeXlsxFile } = await import('write-excel-file/browser');
  const output = writeXlsxFile(buildAttendanceRecordSheets(input));
  await output.toFile(attendanceRecordFileName(input.teamName, input.startDate, input.endDate));
}
